import { useLazyQuery } from "@apollo/client";
import { IonContent, IonItem, IonLabel, IonList, IonMenu } from "@ionic/react";
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router";

import Dot from "@components/Dot";
import styles from "@components/expert/ExpertMenu.module.css";
import ExpertInitialAvatar from "@components/ExpertInitialAvatar";
import Nbsp from "@components/Nbsp";
import Icon from "@components/text/Icon";
import { sessionContext } from "@context/Contexts";
import useSubscribeToAllExperts from "@hooks/subscriptions/useSubscribeToAllExperts";
import useSubscribeToJourneys from "@hooks/subscriptions/useSubscribeToJourneys";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  ExpertGlobalRoleEnum,
  GetExpertsDocument,
  GetExpertsQuery,
  GetExpertsQueryVariables,
  GetExpertUnreadMessageCountDocument,
  GetExpertUnreadMessageCountQuery,
  GetExpertUnreadMessageCountQueryVariables
} from "@typing/Generated";
import { IconName } from "@utils/iconUtils";
import { sortByStringField } from "@utils/modelUtils";

type MenuItemProps = {
  children?: ReactNode;
  disabled?: boolean;
  href?: string;
  iconName?: IconName;
  onClick: () => void;
  routerLink?: string;
  textKey?: string;
  unread?: boolean;
};

const MenuItem = ({
  children,
  disabled = false,
  href,
  iconName,
  onClick,
  routerLink,
  textKey,
  unread = false
}: MenuItemProps) => {
  const location = useLocation();
  const intl = useIntl();

  const className =
    (routerLink && location.pathname.includes(routerLink) ? styles.selectedMenuItem : "") + " " + styles.menuItem;

  return (
    <IonItem
      button
      className={className}
      data-test="nav-item"
      disabled={disabled}
      href={href}
      lines="none"
      onClick={onClick}
      rel={href ? "noopener,noreferrer" : ""}
      routerLink={routerLink}
    >
      {children ? (
        <>{children}</>
      ) : (
        <>
          {iconName && (
            <Icon
              aria-label={intl.formatMessage({ id: `menus.expert.${textKey ?? ""}` })}
              color="primary-300-shade"
              icon={iconName}
            />
          )}

          <IonLabel>{intl.formatMessage({ id: `menus.expert.${textKey ?? ""}` })}</IonLabel>
        </>
      )}
      <Dot unread={unread} />
    </IonItem>
  );
};

const ExpertMenu = () => {
  const { sessionState } = useContext(sessionContext);
  const menuRef = useRef<HTMLIonMenuElement>(null);
  const history = useHistory();
  const isAdmin = sessionState.impersonatingExpert;
  const myExpert = useMyExpert();

  const [getExperts, getExpertsQuery] = useLazyQuery<GetExpertsQuery, GetExpertsQueryVariables>(GetExpertsDocument);

  const [getMessageCount, getMessageCountQuery] = useLazyQuery<
    GetExpertUnreadMessageCountQuery,
    GetExpertUnreadMessageCountQueryVariables
  >(GetExpertUnreadMessageCountDocument);

  useEffect(() => {
    if (myExpert?.id) {
      getMessageCount({
        variables: {
          expertId: myExpert.id
        }
      });
    }
  }, [getMessageCount, myExpert?.id]);

  useSubscribeToAllExperts(
    () => {
      if (myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER) {
        getExpertsQuery.refetch();
      }
    },
    { skip: myExpert?.expertGlobalRole !== ExpertGlobalRoleEnum.MANAGER }
  );

  useSubscribeToJourneys(() => {
    getMessageCountQuery.refetch();
  });

  useEffect(() => {
    if (myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER) {
      getExperts();
    }
  }, [getExperts, myExpert, myExpert?.expertGlobalRole]);

  useEffect(() => {
    if (getExpertsQuery.data?.experts.pageInfo.hasNextPage) {
      getExpertsQuery.fetchMore({ variables: { cursor: getExpertsQuery.data.experts.pageInfo.endCursor ?? "" } });
    }
  }, [getExpertsQuery]);

  const allExperts = useNodesFromConnection(getExpertsQuery.data?.experts);
  const managedExperts = useMemo(() => {
    const legitExperts = allExperts.filter(expert => !expert.ignored);
    return sortByStringField(legitExperts, expert => expert.lastName);
  }, [allExperts]);

  const hasUnreadMessages = useMemo(() => {
    if (getMessageCountQuery.data?.expertSummary && getMessageCountQuery.data.expertSummary.unreadMessages > 0) {
      return true;
    }
    return false;
  }, [getMessageCountQuery.data?.expertSummary]);

  const closeMenu = useCallback(() => {
    if (menuRef.current) {
      menuRef.current.close();
    }
  }, [menuRef]);

  const handleAdminClick = useCallback(() => {
    closeMenu();
    history.push("/admin/end-impersonation");
  }, [closeMenu, history]);

  const universalSearchOpener = useGlobalOpener(OpenerTypeEnum.UNIVERSAL_SEARCH);

  const openSearch = useCallback(() => {
    universalSearchOpener.open();
  }, [universalSearchOpener]);

  return (
    <IonMenu className={styles.menu} contentId="main" ref={menuRef}>
      <IonContent>
        <IonList>
          <MenuItem iconName="search" onClick={openSearch} textKey="search" />
          <MenuItem
            iconName="columns"
            onClick={closeMenu}
            routerLink="/expert/dashboard"
            textKey="dashboard"
            unread={hasUnreadMessages}
          />
          <MenuItem iconName="clipboardListCheck" onClick={closeMenu} routerLink="/expert/tasks" textKey="tasks" />
          <MenuItem iconName="calendar" onClick={closeMenu} routerLink="/expert/consults" textKey="consults" />
          <MenuItem iconName="history" onClick={closeMenu} routerLink="/expert/activities" textKey="activity" />
          <MenuItem iconName="handsHeart" onClick={closeMenu} routerLink="/expert/care-plans" textKey="carePlans" />
          <MenuItem iconName="users" onClick={closeMenu} routerLink="/expert/journeys" textKey="journeyList" />
          <MenuItem href="https://gather.withgrayce.com" iconName="handHeart" onClick={closeMenu} textKey="gather" />
          <MenuItem iconName="clock" onClick={closeMenu} routerLink="/expert/time-logs" textKey="timeLogs" />
          <MenuItem iconName="plane" onClick={closeMenu} routerLink="/expert/ooo-schedule" textKey="outOfOffice" />
          <MenuItem iconName="building" onClick={closeMenu} routerLink="/expert/employers" textKey="employers" />
          <MenuItem iconName="tags" onClick={closeMenu} routerLink="/expert/tags" textKey="tags" />

          {/* <MenuItem iconSrc="solid/user-circle" routerLink="/expert/account" /> */}
          {isAdmin && <MenuItem iconName="crown" onClick={handleAdminClick} textKey="admin" />}
          {!isAdmin && (
            <MenuItem iconName="arrowRightFromBracket" onClick={closeMenu} routerLink="/sign-out" textKey="signOut" />
          )}
          {myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER && (
            <>
              <hr className={styles.divider} />
              <MenuItem
                iconName="usersCrown"
                onClick={closeMenu}
                routerLink="/expert/all-journeys"
                textKey="allJourneyList"
              />
              <MenuItem iconName="envelope" onClick={closeMenu} routerLink="/expert/messages" textKey="messageList" />
              <MenuItem
                iconName="wandMagic"
                onClick={closeMenu}
                routerLink="/expert/automations"
                textKey="automations"
              />
              <MenuItem
                iconName="bullseyeArrow"
                onClick={closeMenu}
                routerLink="/expert/goal-templates"
                textKey="goalList"
              />
              <MenuItem
                iconName="bookCopy"
                onClick={closeMenu}
                routerLink="/expert/resource-feature-templates"
                textKey="resourceFeatureTemplates"
              />
              <MenuItem
                iconName="tableLayout"
                onClick={closeMenu}
                routerLink="/expert/message-templates"
                textKey="messageTemplates"
              />
              <MenuItem iconName="tags" onClick={closeMenu} routerLink="/expert/manage-tags" textKey="manageTags" />
              <MenuItem
                iconName="clipboardUser"
                onClick={closeMenu}
                routerLink="/expert/manage-experts"
                textKey="expertList"
              />
              {managedExperts.map(managedExpert => (
                <MenuItem
                  key={managedExpert.id}
                  onClick={closeMenu}
                  routerLink={`/expert/manage-experts/${managedExpert.id}/journeys`}
                >
                  <ExpertInitialAvatar expert={managedExpert} size="tiny" />
                  <IonLabel className={styles.expertName} tabIndex={0}>
                    {managedExpert.firstName}
                    <Nbsp />
                    {managedExpert.lastName[0]}
                  </IonLabel>
                </MenuItem>
              ))}
            </>
          )}
        </IonList>
      </IonContent>
    </IonMenu>
  );
};

export default ExpertMenu;
